import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, Button, Grid, FormControl, RadioGroup, FormControlLabel, Radio, Typography } from "@mui/material";
import { Container, Content, ContentText, RadioGroupContainer } from "./styles";
import LoadingOverlay from "../../../../../containers/LoadingOverlay/LoadingOverlay";
import DialogTitle from "../../../../../containers/DialogTitle/DialogTitle";
import DialogActions from "../../../../../containers/DialogActions/DialogActions";
import { ExpenseReportDTO } from "../../../../../models/expense-reports";
import { ExpenseReportView, fetchFlagImagesAsBase64, uniqueCountyCodes } from "../../utils";
import { EmployeeRef } from "../../../../../models/employees";
import {
  CONTENT_GAP,
  exportExpenseReportsToPDF,
  exportSingleExpenseReportsWithDocs,
  HEADER_FOR_PDF,
} from "../../../../../utils/export-expense-reports-utils/export-expense-reports-as-pdf";
import { LegalEntityBlock, StatusAmountBlock, StatusAmountContainer } from "../styles";
import ExpenseReportContent from "../ExpenseReportContent/ExpenseReportContent";
import { getNormaleDateOrMonth, intlFormatDateWithHour } from "../../../../../utils/time-utils";
import { useAppSelector } from "../../../../../custom-hooks/redux/redux";
import { DocumentFileRef } from "../../../../../models/documents/document-files";
import { getFileExtension } from "../../../../../utils/files-utils/get-file-extension";
import { EXPENSE_REPORT_STATUS } from "../../../../../utils/constants/constants";
import { TravelExpenseAuditNotesApiService } from "src/services/travel-expense-audit-notes/travel-expense-audit-notes-api.service";
import { TravelExpenseAuditNotesFilter } from "src/models/travel-expense-audit-notes";

enum Options {
  without_docs = "Export without documents",
  all = "Export with receipts and business purpose documents",
  receipt_docs = "Export with receipts documents",
  justification_preapproval_docs = "Export with business purpose documents",
}

const optionsToExport = [
  Options.without_docs,
  Options.all,
  Options.receipt_docs,
  Options.justification_preapproval_docs,
];

interface Props {
  disabled: boolean;
  expenseReport: ExpenseReportDTO;
  view: ExpenseReportView;
  employee?: EmployeeRef;
  hideAuditNotes: boolean;
}

interface State {
  open: boolean;
  loading: boolean;
  selectedOption: Options;
  error: string;
}

const ExportSingleExpenseReport = ({ disabled, expenseReport, view, employee, hideAuditNotes }: Props) => {
  console.log(expenseReport);
  const { t } = useTranslation();

  const { currentUserAuthorizations } = useAppSelector((state) => state.currentUserReducer);

  const [state, setState] = useState<State>({
    open: false,
    loading: false,
    selectedOption: Options.without_docs,
    error: "",
  });

  const handleClose = () => {
    setState((state) => ({
      ...state,
      open: false,
      selectedOption: Options.without_docs,
      loading: false,
      error: "",
    }));
  };

  const loadAllAuditNotes = useCallback(async () => {
    try {
      const response = await new TravelExpenseAuditNotesApiService().loadItems(
        {
          travelExpenseIds: [
            ...expenseReport.travelExpenses
              .filter((travelExpense) => travelExpense.includeNotesInExportedReport)
              .map((travelExpense) => travelExpense.id),
          ],
        } as TravelExpenseAuditNotesFilter,
        undefined,
        undefined,
        {
          pageNumber: 0,
          pageSize: 1000,
        },
        undefined,
        60,
        undefined
      );

      return response.items;
    } catch (error) {
      return [];
    }
  }, [expenseReport.travelExpenses]);

  const exportToPDF = useCallback(async () => {
    const allCountryCodes = expenseReport.travelExpenses
      .map((receipt) => {
        return receipt.country?.code;
      })
      .filter((code) => !!code) as string[];

    const preloadedFlags = await fetchFlagImagesAsBase64(uniqueCountyCodes(allCountryCodes));

    const contentToExport = async () => {
      const allAuditNotes = await loadAllAuditNotes();
      return (
        <Grid sx={{ display: "flex", flexDirection: "column", gap: `${CONTENT_GAP}px` }}>
          <Grid
            sx={{ display: "flex", flexDirection: "column", gap: "10px" }}
            className={HEADER_FOR_PDF}
          >
            <Grid>
              <Typography
                variant="h4"
                sx={{ fontWeight: "bold" }}
              >
                {`Expense report for ${employee?.name ?? ""}`}
              </Typography>
              {`Report date ${intlFormatDateWithHour(new Date())}`}
            </Grid>

            <LegalEntityBlock>
              <Typography
                variant={"h6"}
                sx={{ fontWeight: "bold" }}
              >
                {`${t("common_words.legal_entity")}`} :
              </Typography>
              <Typography variant={"h6"}>{expenseReport.legalEntity?.name ?? ""}</Typography>
            </LegalEntityBlock>

            <StatusAmountContainer>
              <StatusAmountBlock>
                <Typography sx={{ fontWeight: "bold" }}>Status:</Typography>
                <Typography>{expenseReport.status?.name}</Typography>
                {expenseReport.documentation &&
                (expenseReport.status?.id === EXPENSE_REPORT_STATUS.submitted.id ||
                  expenseReport.status?.id === EXPENSE_REPORT_STATUS.returned_to_employee.id) ? (
                  <Typography variant="body2">(Documentation audit marked as OK)</Typography>
                ) : (
                  ""
                )}
              </StatusAmountBlock>

              <StatusAmountBlock>
                <Typography sx={{ fontWeight: "bold" }}>Total:</Typography>
                <Typography>{expenseReport.totalAmount?.toLocaleCustom()}</Typography>
                <Typography>{expenseReport.currency?.code ?? ""}</Typography>
              </StatusAmountBlock>
            </StatusAmountContainer>
          </Grid>

          <ExpenseReportContent
            employee={expenseReport.employee as EmployeeRef}
            expenseReport={expenseReport}
            view={view}
            wholeReportIsReadOnly
            hideAuditNotes={hideAuditNotes}
            preloadedFlags={preloadedFlags}
            forExport
            currUserAuth={currentUserAuthorizations}
            allAuditNotes={allAuditNotes}
          />
        </Grid>
      );
    };

    state.selectedOption === Options.without_docs &&
      (await exportExpenseReportsToPDF(
        await contentToExport(),
        () => {
          setState((state) => ({
            ...state,
            error: "Error generateing PDF file",
          }));

          return;
        },
        `${new Date(expenseReport.date).getFullYear()} ${getNormaleDateOrMonth(
          new Date(expenseReport.date).getMonth() + 1
        )} ${getNormaleDateOrMonth(new Date(expenseReport.date).getDate())} Expense note ${
          expenseReport.legalEntity?.code
        } ${expenseReport.employee?.name} ${expenseReport.totalAmount} ${expenseReport.currency?.code ?? ""}`
      ));

    if (state.selectedOption !== Options.without_docs) {
      const docFiles: { file: DocumentFileRef; customFileName: string }[] = [];

      if (state.selectedOption === Options.receipt_docs) {
        expenseReport.travelExpenses.forEach((report) => {
          report.receiptsDocument?.files?.forEach((file) => {
            const fileExtension = getFileExtension(file.name).toLowerCase();

            const newFileName = `${new Date(report.date).getFullYear()} ${getNormaleDateOrMonth(
              new Date(report.date).getMonth() + 1
            )} ${getNormaleDateOrMonth(new Date(report.date).getDate())} ${expenseReport?.employee?.name ?? ""} ${
              report?.type?.name
            } ${report.description} ${report.amount} ${report.currency?.code ?? ""}`;

            docFiles.push({ file: file, customFileName: `${newFileName}.${fileExtension}` });
          });
        });
      } else if (state.selectedOption === Options.justification_preapproval_docs) {
        expenseReport.preApprovalDocument?.files?.forEach((file) => {
          const fileExtension = getFileExtension(file.name).toLowerCase();

          const newFileName = `${new Date(expenseReport.date).getFullYear()} ${getNormaleDateOrMonth(
            new Date(expenseReport.date).getMonth() + 1
          )} ${getNormaleDateOrMonth(new Date(expenseReport.date).getDate())} ${
            expenseReport?.legalEntity?.code
          } Expense note justification ${employee?.name ?? ""} ${expenseReport?.totalAmount} ${
            expenseReport?.currency?.code ?? ""
          }`;

          docFiles.push({ file: file, customFileName: `${newFileName}.${fileExtension}` });
        });
      } else {
        expenseReport.preApprovalDocument?.files?.forEach((file) => {
          const fileExtension = getFileExtension(file.name).toLowerCase();

          const newFileName = `${new Date(expenseReport.date).getFullYear()} ${getNormaleDateOrMonth(
            new Date(expenseReport.date).getMonth() + 1
          )} ${getNormaleDateOrMonth(new Date(expenseReport.date).getDate())} ${
            expenseReport?.legalEntity?.code
          } Expense note justification ${employee?.name ?? ""} ${expenseReport?.totalAmount} ${
            expenseReport?.currency?.code ?? ""
          }`;

          docFiles.push({ file: file, customFileName: `${newFileName}.${fileExtension}` });
        });

        expenseReport.travelExpenses.forEach((report) => {
          report.receiptsDocument?.files?.forEach((file) => {
            const fileExtension = getFileExtension(file.name).toLowerCase();

            const newFileName = `${new Date(report.date).getFullYear()} ${getNormaleDateOrMonth(
              new Date(report.date).getMonth() + 1
            )} ${getNormaleDateOrMonth(new Date(report.date).getDate())} ${expenseReport?.legalEntity?.code} ${
              employee?.name ?? ""
            } ${report?.type?.name} ${report.description} ${report.amount} ${report.currency?.code ?? ""}`;

            docFiles.push({ file: file, customFileName: `${newFileName}.${fileExtension}` });
          });
        });
      }

      await exportSingleExpenseReportsWithDocs(
        await contentToExport(),
        () => {
          setState((state) => ({
            ...state,
            error: "Error generateing PDF file",
          }));

          return;
        },
        `${new Date(expenseReport.date).getFullYear()} ${getNormaleDateOrMonth(
          new Date(expenseReport.date).getMonth() + 1
        )} ${getNormaleDateOrMonth(new Date(expenseReport.date).getDate())} ${
          expenseReport?.legalEntity?.code
        } Expense note  ${expenseReport.employee?.name} ${expenseReport.totalAmount} ${
          expenseReport.currency?.code ?? ""
        }`,
        `${new Date(expenseReport.date).getFullYear()} ${getNormaleDateOrMonth(
          new Date(expenseReport.date).getMonth() + 1
        )} ${getNormaleDateOrMonth(new Date(expenseReport.date).getDate())} ${
          expenseReport?.legalEntity?.code
        } Expense note  ${expenseReport.employee?.name} ${expenseReport.totalAmount} ${
          expenseReport.currency?.code ?? ""
        }`,
        docFiles
      );
    }
  }, [view, expenseReport, state.selectedOption]);

  const handleExport = useCallback(async () => {
    setState((state) => ({
      ...state,
      loading: true,
    }));

    try {
      await exportToPDF();

      handleClose();
    } catch (error) {
      console.log("Error: ", error);

      setState((state) => ({
        ...state,
        error: `Error generating file`,
      }));
    } finally {
      setState((state) => ({
        ...state,
        loading: false,
      }));
    }
  }, [state.selectedOption, exportToPDF]);

  return (
    <>
      <Button
        title={`${t(`common_words.export`)}`}
        variant="contained"
        onClick={() => {
          setState((state) => ({
            ...state,
            open: true,
          }));
        }}
        sx={{ textWrap: "nowrap", textTransform: "none", padding: "5px", height: "25px" }}
        disabled={disabled}
      >
        {`${t(`common_words.export`)}`}
      </Button>

      <Dialog
        open={state.open}
        onClose={handleClose}
      >
        <LoadingOverlay loading={state.loading}>
          <DialogTitle
            title={"Export receipts as PDF"}
            handleClose={handleClose}
          />
          <Content>
            <Container>
              <Grid sx={{ padding: "5px" }}>
                <ContentText>Select how to export:</ContentText>
              </Grid>

              <RadioGroupContainer>
                <FormControl>
                  <RadioGroup
                    aria-labelledby="radio-group"
                    defaultValue={optionsToExport[0]}
                    name="radio-buttons-group"
                    onChange={(_, value) => {
                      setState((state) => ({
                        ...state,
                        selectedOption: value as Options,
                      }));
                    }}
                  >
                    {optionsToExport.map((option) => {
                      return (
                        <FormControlLabel
                          key={option}
                          value={option}
                          control={<Radio />}
                          label={option}
                        />
                      );
                    })}
                  </RadioGroup>
                </FormControl>
              </RadioGroupContainer>
            </Container>
          </Content>

          <DialogActions
            handleCancel={handleClose}
            useGoback
            error={state.error}
          >
            <Button
              onClick={handleExport}
              variant="contained"
            >
              {`${t(`buttons.confirm`)}`}
            </Button>
          </DialogActions>
        </LoadingOverlay>
      </Dialog>
    </>
  );
};

export default ExportSingleExpenseReport;
